'use client';
import React, {useEffect, useState} from 'react';
import {useForm, Controller, useWatch} from 'react-hook-form';

import {yupResolver} from '@hookform/resolvers/yup';
import Button from '@components/Button';
import {
  FORM_DATA_INITIAL_VALUES,
  LEAD_GENERATION_FORM_TYPE,
  validationSchema,
  MENU_ITEM_PROPS_FOR_PREFERED_LANGUAGES,
} from './LeadGenerationForm.constants';
import {
  formData,
  fieldType,
  LeadGenerationFormProps,
} from './LeadGenerationForm.type';
import {requestLeadGenerationAPI} from '@services/properties/requestLeadGeneration';
import {CheckboxElement} from '@components/Checkbox';
import {
  GetLeadGenerationOptionsRO,
  getCountryCodeAndNameFromValue,
  getLeadGenerationOptions,
} from '@services/properties/getLeadGenerationOptions';
import {CountrySelect} from '@components/PhoneInput';
import {ValidationError} from './ValidationError';
import {Select} from './Select';
import {Input} from './Input';
import {Phone} from './Phone';
import {ModalLoader} from '@components/ModalLoader';
import {hasCaptchaSiteKey} from '@utils/getCaptchaSiteKey';
import {useUpdateRecaptcha} from '@damascus-components/Recaptcha/Recaptcha';
import {AutoCompleteElement} from '@damascus-components/AutoCompleteElement';
import {trackFormEvents} from '@utils/googleAnalytics/googleAnalyticsEvents';
import {getFormDataCache, setFormDataCache} from '@utils/formDataCache';
import Link from 'next/link';
import {useLeadSourceParams} from '@hooks/useLeadSourceParams';
import {datadogLogs} from '@datadog/browser-logs';

import style from './LeadGenerationForm.module.scss';
import {ReserveNowLeadGenerationForm} from '@damascus-components/ReserveNowLeadGenerationForm/ReserveNowLeadGenerationForm';

export const consentLinks = {
  lsq: {
    privacyPolicy: `https://londonsquare.co.uk/privacy-policy-cookies`,
    termsAndCondition: `https://londonsquare.co.uk/terms-and-conditions`,
  },
  aldar: {
    privacyPolicy: `https://www.aldar.com/en/privacy-policy`,
    termsAndCondition: `https://www.aldar.com/en/terms-and-conditions`,
  },
};

export const LeadGenerationForm = ({
  onClose,
  leadGenerationId,
  type,
  ...props
}: LeadGenerationFormProps) => {
  if (props.reserve) {
    return (
      <ReserveNowLeadGenerationForm
        onClose={onClose}
        leadGenerationId={leadGenerationId}
        type={type}
        {...props}
      />
    );
  }
  const [formOptions, setFormOptions] = useState<GetLeadGenerationOptionsRO>({
    countries: [],
    preferredLanguages: [],
    countryCode: [],
    nationalities: [],
    cities: {},
    interests: [],
  });
  const [status, setStatus] = useState({error: false, loading: false});
  const [errorMessage, setErrorMessage] = useState('');
  const [cities, setCities] = useState([]);
  const {getLeadSourceParams} = useLeadSourceParams();
  const {getRecaptchaToken} = useUpdateRecaptcha();

  const formDataCache = React.useMemo(
    () => getFormDataCache(FORM_DATA_INITIAL_VALUES),
    []
  );
  const formStart = React.useRef(false);

  const {
    handleSubmit,
    setValue,
    formState: {errors},
    control,
    trigger,
    watch,
    setError,
  } = useForm<formData>({
    defaultValues: formDataCache,
    resolver: yupResolver(validationSchema),
  });
  const isFormValid = !watch('consent');
  useEffect(() => {
    setValue('consent', false);
  }, []);

  const leadMetaRequest = React.useMemo(() => getLeadGenerationOptions(), []);
  const setLeadMeta = async () => {
    try {
      const leadMeta = await leadMetaRequest.getResponse();
      setFormOptions(leadMeta);
    } catch (e) {
      datadogLogs.logger.error('Failed to fetch lead meta');
    }
  };
  const getLSQConsentFlag = (data: formData) => {
    if (!data || !props.lsqListing) {
      return {
        otherPropertyUpdates: undefined,
        aldarUpdates: undefined,
      };
    }
    return {
      otherPropertyUpdates: data.otherPropertyUpdates,
      aldarUpdates: data.aldarUpdates,
    };
  };

  React.useEffect(() => {
    setLeadMeta();
    return () => leadMetaRequest.abort(); // Cleanup function to abort the request if the component is unmounted
  }, []);

  React.useEffect(() => {
    updateCityList(countryWatch);
    setValue('city', formDataCache.city);
  }, [formOptions]);

  const getReserveKeys = () => {
    if (!props.reserve) return {};

    return {
      ...(type === 'property' && {
        unitID: leadGenerationId,
      }),
      reserveNow: true,
    };
  };

  const resetCityError = () => setError('city', {message: ''});
  const onSubmit = async (data: formData) => {
    if (cities.length !== 0 && (!data.city || data.city === '')) {
      setError('city', {message: 'City is required'});
      return;
    }

    setStatus({error: false, loading: true});
    if (!data.consent) {
      setStatus({error: true, loading: false});
      setErrorMessage('You must accept the terms and conditions');
      return;
    }
    const responseKey = await getRecaptchaToken();
    if (hasCaptchaSiteKey() && !responseKey) {
      setStatus({error: true, loading: false});
      setErrorMessage('Please refresh the page and try again.');
      return;
    }
    setFormDataCache(data);
    const sourceDetails = getLeadSourceParams();
    const reserveKeys = getReserveKeys();
    const payload = {
      ...data,
      ...sourceDetails,
      ...reserveKeys,
      ...getLSQConsentFlag(data),
      countryCode: getCountryCodeAndNameFromValue(data.countryCode).code,
      interestedIn: undefined,
      id: leadGenerationId,
      type: LEAD_GENERATION_FORM_TYPE[
        type as keyof typeof LEAD_GENERATION_FORM_TYPE
      ],
      responseKey: responseKey,
    };
    try {
      const {message} = await requestLeadGenerationAPI(payload).getResponse();
      onClose({
        title: message.title,
        description: message.description,
        image: message.image,
      });
      setStatus({error: false, loading: false});
      trackFormEvents('success');
    } catch (error: any) {
      setStatus({error: true, loading: false});
      setErrorMessage(error);
      trackFormEvents('fail');
    }
  };

  const handleClear = (name: fieldType) => () => setValue(name, '');

  const onChange = (formKey: keyof formData) => (value: string) => {
    if (value === undefined) return;
    setValue(formKey, value);
  };

  function updateCityList(country: string) {
    const newCities: any = formOptions.cities[country];
    setCities(newCities?.length > 0 ? newCities : []);
  }

  const countryWatch = useWatch({
    control,
    name: 'country',
  });
  const countryCodeWatch = useWatch({control, name: 'countryCode'});

  const phoneWatch = useWatch({control, name: 'contact'});

  React.useEffect(() => {
    if (phoneWatch.length > 0) trigger('contact');
  }, [phoneWatch, countryCodeWatch]);

  React.useEffect(() => {
    const selectedOption = formOptions.countryCode.find(
      ({name}) => name === countryWatch
    );
    onChange('countryCode')(selectedOption?.value as string);
    handleClear('city')();
    resetCityError();
    updateCityList(countryWatch);
  }, [countryWatch]);
  React.useEffect(() => {
    const selectedOption = formOptions.countryCode.find(
      ({value}) => value === countryCodeWatch
    );
    onChange('country')(selectedOption?.name as string);
  }, [countryCodeWatch]);

  const handleFormStart = () => {
    if (formStart.current === true) return;
    formStart.current = true;
    trackFormEvents('start');
  };

  return (
    <>
      <ModalLoader loading={status.loading} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={style.formContentWrapper}
        onBlur={handleFormStart}>
        <div className={style.nameBlock}>
          <div>
            <Controller
              render={({field}) => (
                <Input
                  placeholder='First Name'
                  className={style.formInput}
                  autoComplete='given-name'
                  defaultValue={formDataCache.firstName}
                  {...field}
                />
              )}
              rules={{maxLength: 50}}
              name='firstName'
              control={control}
            />
            <ValidationError message={errors.firstName?.message} />
          </div>
          <div>
            <Controller
              render={({field}) => (
                <Input
                  className={style.formInput}
                  placeholder='Last Name'
                  autoComplete='family-name'
                  defaultValue={formDataCache.lastName}
                  {...field}
                />
              )}
              rules={{maxLength: 50}}
              name='lastName'
              control={control}
            />
            <ValidationError message={errors.lastName?.message} />
          </div>
        </div>
        <div>
          <Controller
            render={({field}) => (
              <Phone
                {...field}
                className={style.phoneInput}
                defaultValue={formDataCache.contact}
                countrySelect={
                  <Controller
                    name='countryCode'
                    control={control}
                    render={({field}) => (
                      <CountrySelect
                        {...field}
                        onChange={onChange('countryCode')}
                        options={formOptions.countryCode}
                      />
                    )}
                  />
                }
              />
            )}
            name='contact'
            control={control}
          />
          <ValidationError message={errors.contact?.message} />
        </div>
        <div>
          <Controller
            render={({field}) => (
              <Input
                id='email'
                type='email'
                autoComplete='email'
                className={style.formInput}
                placeholder='Email Address'
                defaultValue={formDataCache.email}
                {...field}
              />
            )}
            name='email'
            control={control}
          />
          <ValidationError message={errors.email?.message} />
        </div>
        <div>
          <Controller
            render={({field}) => (
              <Select
                {...field}
                options={formOptions.countries}
                placeholder='Country of Residence'
                className={style.filterSelectInput}
                handleClose={handleClear('country')}
              />
            )}
            name='country'
            control={control}
          />
          <ValidationError message={errors.country?.message} />
        </div>
        <div>
          <Controller
            render={({field}) => (
              <AutoCompleteElement
                {...field}
                options={cities}
                placeholder='City'
                onChange={(value) => {
                  resetCityError();
                  setValue('city', value ?? '');
                }}
              />
            )}
            name='city'
            control={control}
          />
          <ValidationError message={errors.city?.message} />
        </div>

        <div>
          <Controller
            render={({field}) => (
              <Select
                {...field}
                options={formOptions.nationalities}
                placeholder='Nationality'
                className={style.filterSelectInput}
                handleClose={handleClear('nationality')}
              />
            )}
            name='nationality'
            control={control}
          />
          <ValidationError message={errors.nationality?.message} />
        </div>
        <div>
          <Controller
            render={({field}) => (
              <Select
                {...field}
                options={formOptions.preferredLanguages}
                placeholder='Preferred Language'
                className={style.filterSelectInput}
                handleClose={handleClear('language')}
                menuItemProps={MENU_ITEM_PROPS_FOR_PREFERED_LANGUAGES}
              />
            )}
            name='language'
            control={control}
          />
          <ValidationError message={errors.language?.message} />
        </div>
        {!props.lsqListing && (
          <div>
            <Controller
              render={({field}) => (
                <CheckboxElement
                  isChecked={watch('goldenVisa')}
                  booleanCheck
                  className={style.checkboxWrapper}
                  handleChange={field.onChange}
                  label={`I'm interested in the Golden Visa`}
                />
              )}
              name='goldenVisa'
              control={control}
            />
          </div>
        )}
        {props.lsqListing && (
          <>
            <div>
              <Controller
                render={({field}) => (
                  <CheckboxElement
                    className={style.checkboxWrapper}
                    handleChange={field.onChange}
                    label={
                      "I'm happy to be contacted by London Square about other new homes and news"
                    }
                  />
                )}
                name='otherPropertyUpdates'
                control={control}
              />
            </div>
            <div>
              <Controller
                render={({field}) => (
                  <CheckboxElement
                    className={style.checkboxWrapper}
                    handleChange={field.onChange}
                    label={
                      'I am happy to be contacted by Aldar Properties about news and their other developments'
                    }
                  />
                )}
                name='aldarUpdates'
                control={control}
              />
            </div>
          </>
        )}
        <div>
          <Controller
            render={({field}) => (
              <CheckboxElement
                className={style.checkboxWrapper}
                handleChange={field.onChange}
                label={''}
                labelElement={
                  <>
                    {props.lsqListing
                      ? 'I agree to the London Square '
                      : 'I consent to the '}
                    <Link
                      href={
                        consentLinks[props.lsqListing ? 'lsq' : 'aldar']
                          .privacyPolicy
                      }
                      target={'_blank'}>
                      Privacy Policy
                    </Link>{' '}
                    &{' '}
                    <Link
                      href={
                        consentLinks[props.lsqListing ? 'lsq' : 'aldar']
                          .termsAndCondition
                      }
                      target={'_blank'}>
                      Terms and conditions
                    </Link>{' '}
                  </>
                }
              />
            )}
            name='consent'
            control={control}
          />
        </div>
        <div>
          <span className={style.forminfo}>
            Please note that all of the above fields are mandatory
          </span>
        </div>
        <div className={style.formContentWrapper__buttonWrapper}>
          <Button
            type='submit'
            disabled={isFormValid}
            className={style.formContentWrapper__buttonWrapper__element}>
            Submit
          </Button>
        </div>
        {status.error && (
          <ValidationError
            message={status.error ? errorMessage.toString() : undefined}
          />
        )}
      </form>
    </>
  );
};
